import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  signInWithPopup,
} from "firebase/auth";
import {
  auth,
  updateUserDatabase,
  getUserFromDatabase,
  isAdminEmail,
  googleProvider,
  githubProvider,
} from "shared/utils/firebase";
import { generateUniqueSiliconId } from "shared/utils/siliconId";
import { saveSessionToCookie } from "shared/utils/sessionBridge";
import { isPhoneTaken, isEmailTaken, isGithubTaken, validateEmailForRole } from "shared/utils/userValidation";
import ForgotPasswordModal from "./ForgotPasswordModal";
import PhoneOtpModal from "./PhoneOtpModal";
import OnboardingModal from "./OnboardingModal";
import styles from "./Auth.module.css";

// ─── Password strength calculation ────────────────────────────────────────

const getPasswordStrength = (pwd) => {
  if (!pwd) return { score: 0, text: "", color: "#374151", pct: 0 };
  let score = 0;
  if (pwd.length >= 6) score += 1;
  if (pwd.length >= 8) score += 1;
  if (/[A-Z]/.test(pwd)) score += 1;
  if (/[0-9]/.test(pwd)) score += 1;
  if (/[^A-Za-z0-9]/.test(pwd)) score += 1;

  if (score <= 2) return { score, text: "Weak", color: "#ef4444", pct: 33 };
  if (score <= 4) return { score, text: "Medium", color: "#f59e0b", pct: 66 };
  return { score, text: "Strong", color: "#10b981", pct: 100 };
};

// ─── Eye-toggle icon ───────────────────────────────────────────────────────

function EyeIcon({ visible, onToggle }) {
  return (
    <button
      type="button"
      className={styles.eyeToggle}
      onClick={onToggle}
      tabIndex={-1}
      aria-label={visible ? "Hide password" : "Show password"}
    >
      {visible ? (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      )}
    </button>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────

function Auth(props) {
  const isSignup = props.signup ? true : false;
  const navigate = useNavigate();

  const [authMethod, setAuthMethod] = useState("email"); // "email" | "phone"
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [socialLoading, setSocialLoading] = useState("");
  const [onboardingUser, setOnboardingUser] = useState(null);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);

  const [values, setValues] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    role: "Student",
  });

  const [errorMsg, setErrorMsg] = useState("");
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
  const [emailValidationStatus, setEmailValidationStatus] = useState({ valid: true, message: "" });
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const emailCheckTimeoutRef = useRef(null);
  const [phoneValidationStatus, setPhoneValidationStatus] = useState({ valid: true, message: "" });
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const phoneCheckTimeoutRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const set = (field) => (e) =>
    setValues((prev) => ({ ...prev, [field]: e.target.value }));

  const handlePhoneChange = (phone) => {
    setValues((prev) => ({ ...prev, phone }));

    if (phoneCheckTimeoutRef.current) {
      clearTimeout(phoneCheckTimeoutRef.current);
    }

    if (!isSignup) {
      setPhoneValidationStatus({ valid: true, message: "" });
      return;
    }

    const digits = phone.replace(/\D/g, "");
    if (!digits) {
      setPhoneValidationStatus({ valid: false, message: "Mobile number is required*" });
      return;
    }

    if (digits.length < 10) {
      setPhoneValidationStatus({
        valid: false,
        message: "Please enter a valid 10-digit mobile number",
      });
      return;
    }

    // Debounced check against Firestore
    setIsCheckingPhone(true);
    setPhoneValidationStatus({ valid: true, message: "Checking availability..." });

    phoneCheckTimeoutRef.current = setTimeout(async () => {
      try {
        const taken = await isPhoneTaken(phone);
        setIsCheckingPhone(false);
        if (taken) {
          setPhoneValidationStatus({
            valid: false,
            message: "⚠️ This mobile number is already registered. Please sign in instead.",
            isDuplicate: true,
          });
        } else {
          setPhoneValidationStatus({
            valid: true,
            message: "✓ Mobile number is available",
            isDuplicate: false,
          });
        }
      } catch (e) {
        setIsCheckingPhone(false);
      }
    }, 350);
  };

  const handleEmailChange = (email) => {
    setValues((prev) => ({ ...prev, email }));

    if (emailCheckTimeoutRef.current) {
      clearTimeout(emailCheckTimeoutRef.current);
    }

    if (!isSignup) {
      setEmailValidationStatus({ valid: true, message: "" });
      return;
    }

    if (!email || !email.trim()) {
      setEmailValidationStatus({ valid: false, message: "Email address is required*" });
      return;
    }

    const roleValidation = validateEmailForRole(email, values.role);
    if (!roleValidation.valid) {
      setEmailValidationStatus(roleValidation);
      return;
    }

    // Debounced check against Firestore
    setIsCheckingEmail(true);
    setEmailValidationStatus({
      valid: true,
      message: "Checking availability...",
      isEducational: roleValidation.isEducational,
    });

    emailCheckTimeoutRef.current = setTimeout(async () => {
      try {
        const taken = await isEmailTaken(email);
        setIsCheckingEmail(false);
        if (taken) {
          setEmailValidationStatus({
            valid: false,
            message: "⚠️ This email is already registered. Please sign in instead.",
            isDuplicate: true,
            isEducational: false,
          });
        } else {
          setEmailValidationStatus({
            valid: true,
            message: roleValidation.isEducational
              ? `🎓 Educational email verified and available for ${values.role}`
              : `✓ Email is available and valid for ${values.role}`,
            isDuplicate: false,
            isEducational: roleValidation.isEducational,
          });
        }
      } catch (e) {
        setIsCheckingEmail(false);
      }
    }, 350);
  };

  const handleRoleChange = (role) => {
    setValues((prev) => ({ ...prev, role }));
    if (isSignup && values.email) {
      if (emailCheckTimeoutRef.current) clearTimeout(emailCheckTimeoutRef.current);
      const roleValidation = validateEmailForRole(values.email, role);
      if (!roleValidation.valid) {
        setEmailValidationStatus(roleValidation);
        return;
      }
      setIsCheckingEmail(true);
      emailCheckTimeoutRef.current = setTimeout(async () => {
        try {
          const taken = await isEmailTaken(values.email);
          setIsCheckingEmail(false);
          if (taken) {
            setEmailValidationStatus({
              valid: false,
              message: "⚠️ This email is already registered. Please sign in instead.",
              isDuplicate: true,
              isEducational: false,
            });
          } else {
            setEmailValidationStatus({
              valid: true,
              message: roleValidation.isEducational
                ? `🎓 Educational email verified and available for ${role}`
                : `✓ Email is available and valid for ${role}`,
              isDuplicate: false,
              isEducational: roleValidation.isEducational,
            });
          }
        } catch (e) {
          setIsCheckingEmail(false);
        }
      }, 350);
    }
  };

  // ── Social OAuth (Google & GitHub) ────────────────────────────────────────

  const handleSocialAuth = async (provider, providerName) => {
    setSocialLoading(providerName);
    setErrorMsg("");

    try {
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;

      if (!user) {
        throw new Error("No user returned from social authentication.");
      }

      console.log(`✅ [${providerName}] Authentication successful for UID:`, user.uid);
      let userData = await getUserFromDatabase(user.uid);
      const isRoleAdmin = isAdminEmail(user.email);

      if (!userData) {
        const userEmail = (user.email || "").toLowerCase();
        let githubUser = "";
        let githubId = "";
        if (providerName.toLowerCase() === "github") {
          githubUser = user.reloadUserInfo?.screenName || "";
          const ghData = user.providerData?.find((p) => p.providerId === "github.com");
          githubId = ghData?.uid || "";
        }

        // 1. Deny multiple accounts with same email
        if (userEmail) {
          const emailTaken = await isEmailTaken(userEmail, user.uid);
          if (emailTaken) {
            await signOut(auth);
            setSocialLoading("");
            setErrorMsg(`An account with the email (${userEmail}) is already registered with another sign-in method. Please sign in using your original method.`);
            return;
          }
        }

        // 2. Deny multiple accounts with same GitHub account
        if (githubUser || githubId) {
          const ghTaken = (await isGithubTaken(githubUser, user.uid)) || (githubId && await isGithubTaken(githubId, user.uid));
          if (ghTaken) {
            await signOut(auth);
            setSocialLoading("");
            setErrorMsg("This GitHub account is already linked to another SiliconLogix account. Please sign in with your original account.");
            return;
          }
        }

        // 3. Deny multiple accounts with same phone
        if (user.phoneNumber) {
          const phoneTaken = await isPhoneTaken(user.phoneNumber, user.uid);
          if (phoneTaken) {
            await signOut(auth);
            setSocialLoading("");
            setErrorMsg("The phone number associated with this account is already registered to another user.");
            return;
          }
        }

        // First-time social signup: Mint candidate Silicon ID and require compulsory onboarding
        const assignedSiliconId = await generateUniqueSiliconId();
        const userName = user.displayName || user.reloadUserInfo?.screenName || (userEmail ? userEmail.split("@")[0] : "");

        const newUserData = {
          uid: user.uid,
          email: userEmail,
          name: userName,
          photoURL: user.photoURL || "",
          phone: user.phoneNumber || "",
          siliconId: assignedSiliconId,
          role: isRoleAdmin ? "Admin" : "Student",
          isAdmin: isRoleAdmin,
          createdAt: new Date(),
          lastLogin: new Date(),
          isActive: true,
          welcomeEmailSent: false,
          onboardingCompleted: false,
          provider: providerName.toLowerCase(),
          githubUsername: githubUser,
          githubId: githubId,
        };

        await updateUserDatabase(newUserData, user.uid);
        userData = newUserData;
      } else {
        // Existing user: ensure Silicon ID exists
        if (!userData.siliconId) {
          const assignedSiliconId = await generateUniqueSiliconId();
          await updateUserDatabase({ siliconId: assignedSiliconId }, user.uid);
          userData.siliconId = assignedSiliconId;
        }
      }

      // Check account status
      if (userData?.deleted === true) {
        await signOut(auth);
        setSocialLoading("");
        setErrorMsg("This account has been deleted. Please contact support.");
        return;
      }

      // Save cross-subdomain session bridge
      try {
        saveSessionToCookie({
          ...user,
          ...userData,
          toJSON: () => user.toJSON(),
        });
      } catch (e) { }

      // Broadcast profile update
      window.dispatchEvent(
        new CustomEvent("slx_user_profile_updated", { detail: userData })
      );

      setSocialLoading("");

      // Check if compulsory onboarding is required (first-time signup or incomplete profile)
      const needsOnboarding = Boolean(
        userData?.onboardingCompleted === false ||
        (!userData?.phone && !isRoleAdmin) ||
        (!userData?.email)
      );

      if (needsOnboarding) {
        setOnboardingUser({
          ...userData,
          provider: providerName.toLowerCase(),
        });
        setIsOnboardingOpen(true);
        return;
      }

      if (userData?.role === "Admin" || userData?.isAdmin === true) {
        navigate("/admin");
      } else {
        navigate("/ide");
      }
    } catch (err) {
      console.error(`❌ [${providerName}] Error:`, err);
      setSocialLoading("");
      if (err.code === "auth/popup-closed-by-user") {
        return;
      }
      let msg = err.message || `Failed to sign in with ${providerName}.`;
      if (err.code === "auth/account-exists-with-different-credential") {
        msg = "An account already exists with this email using a different sign-in method (e.g. Google or Email/Password). Please sign in using that method.";
      } else if (err.code === "auth/popup-blocked") {
        msg = "The authentication popup was blocked by your browser. Please allow popups for this site and try again.";
      } else if (err.code === "auth/operation-not-allowed") {
        msg = `${providerName} sign-in is not enabled yet in the Firebase Console. Please add Client ID & Secret in Firebase Authentication Providers.`;
      } else if (err.code === "auth/unauthorized-domain") {
        msg = "This domain is not authorized in Firebase Console -> Authentication -> Settings -> Authorized domains.";
      }
      setErrorMsg(msg);
    }
  };

  // ── Login ─────────────────────────────────────────────────────────────────

  const handleLogin = async () => {
    if (!values.email || !values.password) {
      setErrorMsg("All fields required*");
      return;
    }
    setSubmitButtonDisabled(true);
    console.log("🔐 Login attempt for:", values.email);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        values.email,
        values.password
      );
      const user = userCredential.user;

      if (user) {
        let userData = await getUserFromDatabase(user.uid);

        if (!userData) {
          const assignedSiliconId = await generateUniqueSiliconId();
          const newUserData = {
            email: user.email,
            uid: user.uid,
            siliconId: assignedSiliconId,
            role: isAdminEmail(user.email) ? "Admin" : "Student",
            isAdmin: isAdminEmail(user.email),
            createdAt: new Date(),
            lastLogin: new Date(),
            isActive: true,
            name: user.displayName || user.email.split("@")[0],
            phone: "",
            welcomeEmailSent: false,
          };
          await updateUserDatabase(newUserData, user.uid);
          userData = newUserData;
        } else if (!userData.siliconId) {
          // Ensure Silicon ID is assigned
          const assignedSiliconId = await generateUniqueSiliconId();
          await updateUserDatabase({ siliconId: assignedSiliconId }, user.uid);
          userData.siliconId = assignedSiliconId;
        }

        if (userData?.deleted === true) {
          await signOut(auth);
          setSubmitButtonDisabled(false);
          setErrorMsg("This account has been deleted. Please contact support.");
          return;
        }

        if (userData?.deactivated === true) {
          sessionStorage.setItem("accountDeactivated", "true");
          sessionStorage.setItem(
            "deactivationReason",
            userData.deactivationReason || "Account suspended."
          );
        } else {
          sessionStorage.removeItem("accountDeactivated");
          sessionStorage.removeItem("deactivationReason");
        }

        let isAdmin = userData?.role === "Admin" || userData?.isAdmin === true;

        if (!isAdmin && isAdminEmail(user.email)) {
          isAdmin = true;
          await updateUserDatabase(
            { ...userData, role: "Admin", isAdmin: true, updatedAt: new Date() },
            user.uid
          );
        }

        // Save session cookie for cross-subdomain SSO
        try {
          saveSessionToCookie({
            ...user,
            ...userData,
            toJSON: () => user.toJSON(),
          });
        } catch (e) { }

        // Broadcast profile update
        window.dispatchEvent(
          new CustomEvent("slx_user_profile_updated", { detail: userData })
        );

        setSubmitButtonDisabled(false);

        // Check if compulsory onboarding is pending
        if (userData?.onboardingCompleted === false) {
          setOnboardingUser({
            ...userData,
            provider: userData?.provider || "email",
          });
          setIsOnboardingOpen(true);
          return;
        }

        if (isAdmin) {
          navigate("/admin");
        } else {
          navigate("/ide");
        }
      }
    } catch (err) {
      console.error("❌ Login error:", err.code, err.message);
      setSubmitButtonDisabled(false);
      let msg = "Invalid email or password. Please verify your credentials.";
      if (err.code === "auth/user-not-found" || err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        msg = "Invalid email or password.";
      } else if (err.code === "auth/too-many-requests") {
        msg = "Too many failed attempts. Please reset your password or try again later.";
      } else if (err.message) {
        msg = err.message;
      }
      setErrorMsg(msg);
    }
  };

  // ── Signup ────────────────────────────────────────────────────────────────

  const handleSignup = async () => {
    if (!values.name || !values.email || !values.password || !values.role) {
      setErrorMsg("All fields required*");
      return;
    }
    if (!values.phone || !values.phone.trim()) {
      setErrorMsg("Mobile phone number is required*");
      return;
    }
    const cleanPhoneDigits = values.phone.replace(/\D/g, "");
    if (cleanPhoneDigits.length < 10) {
      setErrorMsg("Please enter a valid 10-digit mobile number*");
      return;
    }
    if (phoneValidationStatus.isDuplicate) {
      setErrorMsg("This mobile number is already registered. Please sign in instead.");
      return;
    }
    const emailValidation = validateEmailForRole(values.email, values.role);
    if (!emailValidation.valid) {
      setErrorMsg(emailValidation.message);
      return;
    }
    if (values.password !== values.confirmPassword) {
      setErrorMsg("Passwords do not match*");
      return;
    }
    if (values.password.length < 6) {
      setErrorMsg("Password must be at least 6 characters*");
      return;
    }

    setSubmitButtonDisabled(true);

    try {
      // 1. Strict Duplicate Check: Deny multiple accounts with same mobile number
      const phoneTaken = await isPhoneTaken(values.phone);
      if (phoneTaken) {
        setPhoneValidationStatus({
          valid: false,
          message: "⚠️ This mobile number is already registered. Please sign in instead.",
          isDuplicate: true,
        });
        setErrorMsg("This mobile number is already registered. Please sign in instead.");
        setSubmitButtonDisabled(false);
        return;
      }

      // 2. Strict Duplicate Check: Deny multiple accounts with same email
      const emailTaken = await isEmailTaken(values.email);
      if (emailTaken) {
        setEmailValidationStatus({
          valid: false,
          message: "This email is already registered. Please login instead.",
          isDuplicate: true,
        });
        setErrorMsg("This email is already registered. Please sign in instead.");
        setSubmitButtonDisabled(false);
        return;
      }

      const response = await createUserWithEmailAndPassword(auth, values.email, values.password);
      const userId = response.user.uid;
      const isRoleAdmin = values.role === "Admin" && isAdminEmail(values.email);
      const assignedRole = isRoleAdmin ? "Admin" : (values.role === "Admin" ? "Student" : values.role);

      // Generate institutional Silicon ID
      const assignedSiliconId = await generateUniqueSiliconId();

      const userData = {
        name: values.name.trim(),
        email: values.email.trim().toLowerCase(),
        phone: values.phone.trim(),
        role: assignedRole,
        isAdmin: isRoleAdmin,
        siliconId: assignedSiliconId,
        createdAt: new Date(),
        lastLogin: new Date(),
        isActive: true,
        welcomeEmailSent: false,
        onboardingCompleted: false,
        provider: "email",
        uid: userId,
      };

      // Write user record to Firestore
      await updateUserDatabase(userData, userId);

      // Broadcast profile update immediately
      window.dispatchEvent(new CustomEvent("slx_user_profile_updated", { detail: userData }));

      // Save session cookie for cross-subdomain SSO
      try {
        saveSessionToCookie({
          ...response.user,
          ...userData,
          toJSON: () => response.user.toJSON(),
        });
      } catch (bridgeErr) {
        console.warn("Session bridge sync warning:", bridgeErr);
      }

      setSubmitButtonDisabled(false);

      // Trigger compulsory Onboarding Modal to collect missing credentials
      setOnboardingUser({
        ...userData,
        provider: "email",
      });
      setIsOnboardingOpen(true);
    } catch (err) {
      setSubmitButtonDisabled(false);
      let msg = err.message || "Failed to create account.";
      if (err.code === "auth/email-already-in-use") {
        msg = "This email is already registered. Please sign in instead.";
      }
      setErrorMsg(msg);
    }
  };

  const handleSubmission = (e) => {
    e.preventDefault();
    if (isSignup) handleSignup();
    else handleLogin();
  };

  // ── Email hint text ───────────────────────────────────────────────────────

  const emailHint = () => {
    if (!values.email) {
      if (values.role === "Student") return "Accepted: gmail, yahoo, outlook, etc.";
      if (values.role === "Professional") return "Required: corporate / business email";
      if (values.role === "Organisation") return "Required: educational or corporate email";
      return null;
    }
    if (isCheckingEmail) return "Checking email availability...";
    if (emailValidationStatus.isDuplicate) {
      return "⚠️ This email is already registered. Please sign in instead.";
    }
    if (!emailValidationStatus.valid) return emailValidationStatus.message;
    if (emailValidationStatus.isEducational)
      return `🎓 Educational email verified and available for ${values.role}`;
    return `✓ Email is available and valid for ${values.role}`;
  };

  const emailHintClass =
    !values.email
      ? styles.infoText
      : emailValidationStatus.valid
        ? styles.infoText
        : styles.errorText;

  const pwdStrength = getPasswordStrength(values.password);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {/* Back link */}
        <Link to="/" className={styles.backLink}>
          &larr; Back to Home
        </Link>

        <h2 className={styles.heading}>{isSignup ? "Create Account" : "Sign In to SiliconLogix"}</h2>

        {/* ── Method Tabs (Email vs Phone) ── */}
        <div className={styles.authTabs}>
          <button
            type="button"
            className={`${styles.authTab} ${authMethod === "email" ? styles.activeTab : ""}`}
            onClick={() => setAuthMethod("email")}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
            Email & Password
          </button>
          <button
            type="button"
            className={`${styles.authTab} ${authMethod === "phone" ? styles.activeTab : ""}`}
            onClick={() => {
              setAuthMethod("phone");
              setIsPhoneModalOpen(true);
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
            </svg>
            Mobile SMS OTP
          </button>
        </div>

        {/* ── Social Login Buttons ── */}
        <div className={styles.socialGrid}>
          <button
            type="button"
            className={styles.socialBtn}
            onClick={() => handleSocialAuth(googleProvider, "Google")}
            disabled={socialLoading !== ""}
          >
            <span className={styles.socialIcon}>
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
              </svg>
            </span>
            {socialLoading === "Google" ? "Connecting..." : "Google"}
          </button>

          <button
            type="button"
            className={styles.socialBtn}
            onClick={() => handleSocialAuth(githubProvider, "GitHub")}
            disabled={socialLoading !== ""}
          >
            <span className={styles.socialIcon}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.53 1.032 1.53 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
              </svg>
            </span>
            {socialLoading === "GitHub" ? "Connecting..." : "GitHub"}
          </button>
        </div>

        {/* ── Mobile Phone SMS Trigger Button ── */}
        <button
          type="button"
          className={styles.phoneAuthBtn}
          onClick={() => setIsPhoneModalOpen(true)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
          </svg>
          Continue with Mobile SMS OTP
        </button>

        <div className={styles.divider}>or continue with email</div>

        <form onSubmit={handleSubmission} className={styles.form}>
          {/* ── Signup-only fields ── */}
          {isSignup && (
            <>
              {/* Role selector - First before any other input */}
              <div className={styles.inputGroup} style={{ marginBottom: "1.25rem" }}>
                <span className={styles.sectionLabel} style={{ fontWeight: "600", fontSize: "0.95rem", color: "#1e293b", marginBottom: "0.5rem", display: "block" }}>
                  Select Your Account Role *
                </span>
                <div className={styles.rolesContainer}>
                  {["Student", "Professional", "Organisation"].map((role) => (
                    <label
                      key={role}
                      className={`${styles.radioLabel} ${values.role === role ? styles.activeRadio : ""}`}
                    >
                      <input
                        type="radio"
                        name="auth-role"
                        value={role}
                        checked={values.role === role}
                        onChange={() => handleRoleChange(role)}
                      />
                      {role}
                    </label>
                  ))}
                </div>
              </div>

              {/* Name row */}
              <div className={styles.inputGroup}>
                <label htmlFor="auth-name">Full Name *</label>
                <input
                  id="auth-name"
                  type="text"
                  required
                  placeholder="Your full name"
                  value={values.name}
                  onChange={set("name")}
                  autoComplete="name"
                />
              </div>

              {/* Email */}
              <div className={styles.inputGroup}>
                <label htmlFor="auth-email">
                  Email Address *
                  {isCheckingEmail && (
                    <span className={styles.statusSpinner}>⏳</span>
                  )}
                  {!isCheckingEmail && values.email && (
                    <span className={styles.statusIndicator}>
                      {emailValidationStatus.valid
                        ? emailValidationStatus.isEducational
                          ? "🎓"
                          : "✓"
                        : "✗"}
                    </span>
                  )}
                </label>
                <input
                  id="auth-email"
                  name="username"
                  autoComplete="username"
                  type="email"
                  required
                  placeholder={
                    values.role === "Student"
                      ? "e.g. name@gmail.com"
                      : values.role === "Professional"
                        ? "e.g. name@company.com"
                        : "e.g. contact@institution.edu"
                  }
                  value={values.email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  style={{
                    borderColor:
                      isSignup && values.email && !emailValidationStatus.valid
                        ? "#ef4444"
                        : undefined,
                  }}
                />
                {emailHint() && (
                  <small
                    className={emailHintClass}
                    style={{
                      display: "block",
                      marginTop: "4px",
                      color: !values.email
                        ? "#94a3b8"
                        : emailValidationStatus.valid
                          ? "#10b981"
                          : "#ef4444",
                    }}
                  >
                    {emailHint()}
                  </small>
                )}
              </div>

              {/* Phone */}
              <div className={styles.inputGroup}>
                <label htmlFor="auth-phone">
                  Phone Number *
                  {isCheckingPhone && (
                    <span className={styles.statusSpinner}>⏳</span>
                  )}
                  {!isCheckingPhone && values.phone && (
                    <span className={styles.statusIndicator}>
                      {phoneValidationStatus.valid ? "✓" : "✗"}
                    </span>
                  )}
                </label>
                <input
                  id="auth-phone"
                  name="tel"
                  type="tel"
                  required
                  placeholder="e.g. +91 98765 43210"
                  value={values.phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  autoComplete="tel"
                  style={{
                    borderColor:
                      isSignup && values.phone && !phoneValidationStatus.valid
                        ? "#ef4444"
                        : undefined,
                  }}
                />
                {isSignup && values.phone && (
                  <small
                    className={
                      phoneValidationStatus.valid
                        ? styles.infoText
                        : styles.errorText
                    }
                    style={{
                      display: "block",
                      marginTop: "4px",
                      color: phoneValidationStatus.valid ? "#10b981" : "#ef4444",
                    }}
                  >
                    {phoneValidationStatus.message}
                  </small>
                )}
              </div>

              {/* Password row */}
              <div className={styles.row}>
                <div className={styles.inputGroup}>
                  <label htmlFor="auth-password">Password *</label>
                  <div className={styles.passwordWrapper}>
                    <input
                      id="auth-password"
                      name="password"
                      autoComplete="new-password"
                      type={showPassword ? "text" : "password"}
                      required
                      placeholder="Min 6 characters"
                      value={values.password}
                      onChange={set("password")}
                    />
                    <EyeIcon
                      visible={showPassword}
                      onToggle={() => setShowPassword((v) => !v)}
                    />
                  </div>
                </div>
                <div className={styles.inputGroup}>
                  <label htmlFor="auth-confirm-password">Confirm Password *</label>
                  <div className={styles.passwordWrapper}>
                    <input
                      id="auth-confirm-password"
                      name="confirm-password"
                      autoComplete="new-password"
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      placeholder="Confirm password"
                      value={values.confirmPassword}
                      onChange={set("confirmPassword")}
                    />
                    <EyeIcon
                      visible={showConfirmPassword}
                      onToggle={() => setShowConfirmPassword((v) => !v)}
                    />
                  </div>
                </div>
              </div>

              {/* Password Strength Meter */}
              {values.password && (
                <div className={styles.strengthBarContainer}>
                  <div className={styles.strengthTrack}>
                    <div
                      className={styles.strengthFill}
                      style={{ width: `${pwdStrength.pct}%`, backgroundColor: pwdStrength.color }}
                    ></div>
                  </div>
                  <span className={styles.strengthText} style={{ color: pwdStrength.color }}>
                    Password Strength: {pwdStrength.text}
                  </span>
                </div>
              )}
            </>
          )}

          {/* ── Login-only fields ── */}
          {!isSignup && (
            <>
              <div className={styles.inputGroup}>
                <label htmlFor="auth-login-email">Email Address</label>
                <input
                  id="auth-login-email"
                  name="username"
                  autoComplete="username"
                  type="email"
                  required
                  placeholder="name@example.com"
                  value={values.email}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, email: e.target.value }))
                  }
                />
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="auth-login-password">Password</label>
                <div className={styles.passwordWrapper}>
                  <input
                    id="auth-login-password"
                    name="password"
                    autoComplete="current-password"
                    type={showPassword ? "text" : "password"}
                    required
                    placeholder="Enter your password"
                    value={values.password}
                    onChange={set("password")}
                  />
                  <EyeIcon
                    visible={showPassword}
                    onToggle={() => setShowPassword((v) => !v)}
                  />
                </div>
              </div>

              {/* Forgot Password link */}
              <div className={styles.forgotRow}>
                <button
                  type="button"
                  className={styles.forgotBtn}
                  onClick={() => setIsForgotPasswordOpen(true)}
                >
                  Forgot Password?
                </button>
              </div>
            </>
          )}

          {/* Error banner */}
          {errorMsg && <div className={styles.formError}>{errorMsg}</div>}

          {/* Submit */}
          <button
            type="submit"
            className={styles.submitBtn}
            disabled={
              submitButtonDisabled ||
              (isSignup && !emailValidationStatus.valid)
            }
          >
            {submitButtonDisabled
              ? "Processing..."
              : isSignup
                ? "Create Account & Get Silicon ID"
                : "Sign In"}
          </button>
        </form>

        {/* Bottom link */}
        <div className={styles.bottom}>
          {isSignup ? (
            <p>
              Already have an account? <Link to="/login">Sign in here</Link>
            </p>
          ) : (
            <p>
              New here? <Link to="/signup">Create an account</Link>
            </p>
          )}
        </div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={isForgotPasswordOpen}
        onClose={() => setIsForgotPasswordOpen(false)}
        initialEmail={values.email}
      />

      {/* Phone SMS OTP Modal */}
      <PhoneOtpModal
        isOpen={isPhoneModalOpen}
        onClose={() => {
          setIsPhoneModalOpen(false);
          setAuthMethod("email");
        }}
        mode="login"
        onSuccess={(phoneUserData) => {
          setIsPhoneModalOpen(false);
          if (
            phoneUserData &&
            (phoneUserData.onboardingCompleted === false ||
              !phoneUserData.email ||
              phoneUserData.name?.startsWith("User "))
          ) {
            setOnboardingUser({
              ...phoneUserData,
              provider: "phone",
            });
            setIsOnboardingOpen(true);
          } else if (phoneUserData?.role === "Admin" || phoneUserData?.isAdmin === true) {
            navigate("/admin");
          } else {
            navigate("/ide");
          }
        }}
      />

      {/* Compulsory Onboarding Modal */}
      {isOnboardingOpen && (
        <OnboardingModal
          isOpen={isOnboardingOpen}
          userDetails={onboardingUser}
          onSuccess={(updatedUser) => {
            setIsOnboardingOpen(false);
            setOnboardingUser(null);
            if (updatedUser?.role === "Admin" || updatedUser?.isAdmin === true) {
              navigate("/admin");
            } else {
              navigate("/ide");
            }
          }}
          onCancel={() => {
            setIsOnboardingOpen(false);
            setOnboardingUser(null);
          }}
        />
      )}
    </div>
  );
}

export default Auth;
